/**
 * iter87.217 (user-reported) — MLB Series Predictor pick modal. Was
 * previously a standalone always-visible banner card
 * (`MlbSeriesPickTile.jsx`, now retired) sitting above the Draft
 * section — user flagged that pattern as unfamiliar/unused elsewhere
 * in the app. Moved into a proper Draft-grid IN-SEASON PICKS tile
 * (see `TodoChips.jsx`) + this modal, matching the same
 * tile-opens-modal convention every other pick surface uses
 * (Champions Pick, Power Up, Matchday Boost, etc).
 *
 * Each open series lets the player pick which team advances + the
 * exact deciding game (2/3 for Wild Card, 3/4/5 for Division, 4/5/6/7
 * for Championship/World Series) — scoring: correct team 6pts (+4 if
 * the game is ALSO exact → 10), wrong team -2pts (+2 partial credit if
 * the game alone is exact → nets 0).
 *
 * iter87.268 (user-reported redesign) — projected Wild Card series
 * (built from live standings before the real bracket locks) are now
 * PICKABLE, not just previewable — with a standings-based disclaimer
 * so players know the matchup can still shift. Also added seed chips
 * and a "how this works" blurb since players weren't sure whether to
 * pick every series or just one.
 *
 * iter87.274 (user changed their mind) — team-color swatches are
 * back in this modal (were removed in iter87.262 per an earlier,
 * now-reversed request). Also recolored the "Provisional" banner
 * from blue (volt) to amber so it visually stands out as a distinct
 * warning state, not just another blue accent — same amber used in
 * `PlayoffSeriesIntelModal.jsx`'s matching banner.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Info, AlertTriangle, Check, Lock, ChevronDown, Trophy, X } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { Baseball } from "@phosphor-icons/react";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";
import { missedPlayoffsCopy } from "@/lib/playoffField";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";

function SeedChip({ seed }) {
    return (
        <span className="inline-flex w-6 h-6 items-center justify-center rounded-lg bg-black border border-white/10 font-mono text-[10px] font-bold text-zinc-400 shrink-0">
            {seed ?? "–"}
        </span>
    );
}

// iter87.315 (user-requested — "the locked and completed series should
// show series score somewhere") — games-won tally for the series so
// far, derived from `s.games` (already present on the bracket response
// per game's `played`/`score_a`/`score_b` — `score_a` always maps to
// `team_a_id`, `score_b` to `team_b_id`, regardless of home/away, per
// mlb_engine._apply_match_results). Only meaningful once at least one
// game has actually been decided (locked or final series).
function seriesRecord(s) {
    let aWins = 0, bWins = 0;
    (s.games || []).forEach((g) => {
        if (!g.played) return;
        if ((g.score_a ?? 0) > (g.score_b ?? 0)) aWins++;
        else if ((g.score_b ?? 0) > (g.score_a ?? 0)) bWins++;
    });
    return { aWins, bWins };
}

export default function MlbSeriesPickModal({ open, onClose, tournamentId, participantId, teams, existingPicks, onPicksChanged, tournament }) {
    const [bracket, setBracket] = useState(null);
    const [picks, setPicks] = useState(existingPicks || {});
    const [savingAll, setSavingAll] = useState(false);
    const [clearing, setClearing] = useState(false);
    // iter87.315 (user-requested 3-section redesign) — "Locked & Live"
    // starts OPEN (players still want to glance at in-progress series
    // often); "Completed" starts collapsed (read-only history, checked
    // less often).
    const [showLiveLocked, setShowLiveLocked] = useState(true);
    const [showCompleted, setShowCompleted] = useState(false);

    const teamFor = (id) => (teams || []).find((t) => t.id === id);
    const nameFor = (id) => teamFor(id)?.name || id || "TBD";
    const seedFor = (id) => teamFor(id)?.seed;

    // iter87.300 (user-reported — "Series predictor keeps on resetting
    // and clearing picks") — root cause: this used to resync local
    // `picks` from the `existingPicks` PROP on every render where that
    // prop's reference changed, even while the modal stayed open. The
    // parent's `me` state gets refreshed by the dashboard's own
    // websocket/45s-poll cycle independently of this modal — if that
    // refresh's response reflects a split-second-stale snapshot (e.g.
    // it was already in flight before a pick just saved), it stomped
    // the just-made pick out of the UI the instant it landed, even
    // though the DB copy (`submitPick`'s own `await` already persisted
    // it) was always fine — a purely visual, but very real-looking,
    // "my pick disappeared" bug that could repeat on every stray
    // refresh. Now only resyncs from the prop at OPEN time — a ref
    // captures the latest prop value without re-triggering the effect,
    // so a background refresh landing while the modal is open can no
    // longer clobber in-progress local picks.
    const existingPicksRef = useRef(existingPicks);
    existingPicksRef.current = existingPicks;

    // iter87.311 (user-reported, recurring — "Braves/Phillies should be
    // locked, it's 11:08" but the modal still showed it open) — this
    // used to fetch the bracket (and therefore each series' open/locked/
    // final `status`) ONCE, at modal-open time, and never again. A
    // series that locks (kickoff passes) WHILE the modal stays open —
    // or is opened right before the poller's next tick — kept showing
    // its stale pre-lock status indefinitely. Now refetches on the same
    // 45s cadence the rest of the dashboard already polls at. Does NOT
    // touch `picks` on the refresh tick (only on open) — iter87.300's
    // fix for the separate "picks visually reset" bug stays intact.
    useEffect(() => {
        if (!open || !tournamentId) return;
        const load = () => api.getMlbBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
        load();
        setPicks(existingPicksRef.current || {});
        const id = setInterval(load, 45000);
        return () => clearInterval(id);
    }, [open, tournamentId]);

    if (!open) return null;

    const rounds = bracket?.rounds || [];
    // iter87.299 (user-requested redesign) — no more "one active round
    // at a time, mixed open+locked together": every real/projected
    // series across ALL rounds is split into an always-visible "Open"
    // section (a newly-available series naturally lands here — no
    // extra sort needed) and a collapsible "Locked Series" section
    // below (locked + final, read-only), grouped by round since more
    // than one round can have locked series at once (e.g. a lagging
    // Wild Card series still open while an early Division Series is
    // already set).
    const allSeries = rounds.flatMap((r) =>
        (r.series || [])
            .filter((s) => s.status !== "tbd")
            .map((s) => ({ ...s, _roundKey: r.key, _roundLabel: r.label, _validGames: r.valid_games })));
    const notSetYet = !bracket || allSeries.length === 0;
    // iter87.315 (user-requested) — split what used to be one combined
    // "Locked Series" bucket into two: series that are locked/in-
    // progress but not yet decided ("Locked & Live") and series that
    // have actually concluded ("Completed", read-only score recap).
    const openSeries = allSeries.filter((s) => s.status === "open");
    const liveSeries = allSeries.filter((s) => s.status === "locked");
    const completedSeries = allSeries.filter((s) => s.status === "final");
    const anyProjected = openSeries.some((s) => s.projected);
    const pickableSeries = openSeries;
    const incompleteCount = pickableSeries.filter((s) => {
        const p = picks[s.id] || {};
        return !p.team_id || !p.game_count;
    }).length;
    const byRound = (list) => list.reduce((acc, s) => {
        if (!acc[s._roundKey]) acc[s._roundKey] = { label: s._roundLabel, series: [] };
        acc[s._roundKey].series.push(s);
        return acc;
    }, {});
    const liveByRound = byRound(liveSeries);
    const completedByRound = byRound(completedSeries);

    // iter87.436 (user-reported — "every time a tile is selected, it
    // saves with a green toast, I wanted the Save button to actually
    // do the save") — tapping a team or game-count tile now only
    // updates LOCAL state. Nothing hits the backend, and no toast
    // fires, until "Save Picks" below is tapped. Previously every tile
    // tap fired its own save request + "Series pick saved" toast,
    // which made the tile itself feel like the save action — the
    // explicit "Save Picks" button sitting right below it was
    // redundant and confusing.
    const selectPick = (seriesId, teamId, gameCount) => {
        setPicks((prev) => ({ ...prev, [seriesId]: { team_id: teamId, game_count: gameCount ?? null } }));
    };

    // iter87.277 (user-requested) — "Save Picks" is the explicit final
    // confirm: blocks (with a clear error) if any open series has only
    // a team OR only a game count picked, never both/neither, so a
    // half-finished pick can't be mistaken for a complete one.
    // iter87.436 (user-reported) — this is now the ONLY thing that
    // persists picks to the backend (see `selectPick` above) — submits
    // every open series' pick in one round trip.
    const onSavePicks = async () => {
        const pickable = pickableSeries;
        const incomplete = pickable.filter((s) => {
            const p = picks[s.id] || {};
            return !p.team_id || !p.game_count;
        });
        if (incomplete.length > 0) {
            toast.error(`Pick a team AND a game count for every series before saving — ${incomplete.length} series still incomplete`);
            return;
        }
        setSavingAll(true);
        try {
            await Promise.all(pickable.map((s) => {
                const p = picks[s.id];
                return api.submitMlbSeriesPick(tournamentId, participantId, s.id, p.team_id, p.game_count);
            }));
            onPicksChanged?.(picks);
            toast.success("All picks saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Some picks failed to save — try again");
        } finally {
            setSavingAll(false);
        }
    };

    // iter87.277 — "Clear All Picks": only wipes open (unlocked) series
    // this round, both locally and on the backend (a purely local reset
    // would just get overwritten by `existingPicks` next time the modal
    // opens), so a player can restart a round clean.
    const onClearAll = async () => {
        const pickable = pickableSeries;
        const idsWithPicks = pickable.filter((s) => picks[s.id]?.team_id || picks[s.id]?.game_count).map((s) => s.id);
        if (idsWithPicks.length === 0) {
            toast.info("No picks to clear yet");
            return;
        }
        setClearing(true);
        try {
            await api.clearMlbSeriesPicks(tournamentId, participantId, idsWithPicks);
            const next = { ...picks };
            idsWithPicks.forEach((id) => delete next[id]);
            setPicks(next);
            onPicksChanged?.(next);
            toast.success("Picks cleared");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't clear picks");
        } finally {
            setClearing(false);
        }
    };

    // iter87.299 — shared card renderer for the Open, Locked & Live, and
    // Completed sections — status already drives the disabled/final
    // states, just reused across all three lists now.
    // iter87.315 (user-requested) — Completed cards now render the
    // full team-row/games-pill body (previously replaced entirely by a
    // "Final — X won" text line) so the read-only green/red scoring
    // feedback has somewhere to attach: a green outer card border +
    // green mini-border on your picked team's row when your pick WON
    // (points scored), red on both when it LOST (0/negative net
    // points), neutral grey when you never made a pick on that series
    // at all. The exact-game-count guess gets the same green/red mini-
    // border treatment on its own pill, independent of the team-pick
    // result.
    const renderSeriesCard = (s) => {
        const pick = picks[s.id] || {};
        const locked = s.status !== "open";
        const isFinal = s.status === "final";
        const isLive = s.status === "locked";
        const record = (isFinal || isLive) ? seriesRecord(s) : null;
        const pickedWinner = isFinal && !!pick.team_id && pick.team_id === s.winner_team_id;
        const pickedLoser = isFinal && !!pick.team_id && pick.team_id !== s.winner_team_id;
        const outerBorderClass = pickedWinner ? "border-emerald-500" : pickedLoser ? "border-red-500" : "border-neutral-800";
        return (
            <div
                key={s.id}
                data-testid={`mlb-series-${s.id}`}
                data-result={isFinal ? (pickedWinner ? "correct" : pickedLoser ? "incorrect" : "no-pick") : undefined}
                className={`brutal-card bg-neutral-900 border-2 ${outerBorderClass} p-4 space-y-3`}
            >
                <div className="flex items-center gap-2 flex-wrap">
                    {locked && <Lock className="w-3 h-3 text-zinc-500 shrink-0" data-testid={`mlb-series-${s.id}-locked-icon`} />}
                    {s._roundLabel && (
                        <span className="inline-flex items-center rounded-lg bg-neutral-950 border border-white/10 text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider text-zinc-500">
                            {s._roundLabel}
                        </span>
                    )}
                    {s.league && (
                        <span className="inline-flex items-center rounded-lg bg-neutral-950 border border-white/10 text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider text-zinc-400">
                            {s.league}
                        </span>
                    )}
                    {s.projected && (
                        <span
                            data-testid={`mlb-series-${s.id}-projected-badge`}
                            className="pill bg-neutral-800 text-neutral-400 border border-neutral-700 text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest"
                        >
                            Projected
                        </span>
                    )}
                    {record && (
                        <span data-testid={`mlb-series-${s.id}-record`} className="ml-auto font-mono text-xs font-bold text-zinc-300 tabular-nums">
                            {record.aWins}–{record.bWins}
                        </span>
                    )}
                </div>
                {isFinal && (
                    <p data-testid={`mlb-series-${s.id}-result`} className="text-[10px] uppercase tracking-widest text-zinc-400">
                        Final — <span className="text-cyber font-bold">{nameFor(s.winner_team_id)}</span> won in {s.deciding_game}
                    </p>
                )}
                {/* iter87.282 (user-requested) — same dropout warning
                    copy used everywhere else a picked team falls out of
                    the field, surfaced here too: a pick can silently
                    point at a team that's no longer one of this series'
                    two teams once live seeding shifts. Not relevant once
                    a series is final. */}
                {!isFinal && pick.team_id && pick.team_id !== s.team_a_id && pick.team_id !== s.team_b_id && (
                    <p data-testid={`mlb-series-${s.id}-dropped-notice`} className="text-heat text-[10px] font-semibold flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        {missedPlayoffsCopy(nameFor(pick.team_id), tournament, { locked, subject: "your pick" })}
                    </p>
                )}
                <div>
                    {[[s.team_a_id, s.team_a_seed ?? seedFor(s.team_a_id)], [s.team_b_id, s.team_b_seed ?? seedFor(s.team_b_id)]].map(([tid, seed], idx) => {
                        const isSelected = pick.team_id === tid;
                        let rowClass = isSelected ? "border-volt bg-volt/20" : "border-transparent bg-neutral-800 hover:border-neutral-600";
                        if (isFinal && isSelected) {
                            rowClass = tid === s.winner_team_id ? "border-emerald-500 bg-emerald-500/10" : "border-red-500 bg-red-500/10";
                        }
                        return (
                            <button
                                key={tid || idx}
                                data-testid={`mlb-series-${s.id}-pick-${idx === 0 ? "a" : "b"}`}
                                aria-pressed={isSelected}
                                disabled={locked || !tid}
                                onClick={() => selectPick(s.id, tid, pick.game_count)}
                                className={`flex items-center justify-between w-full p-3 mb-2 rounded-lg border-2 transition-all
                                    ${rowClass}
                                    ${locked && !isFinal ? "opacity-50 cursor-not-allowed" : locked ? "cursor-not-allowed" : ""}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <SeedChip seed={seed} />
                                    {/* iter87.274 (user changed their mind — was
                                        removed in iter87.262, wanted back) */}
                                    <TeamSwatch name={nameFor(tid)} sport="mlb" size="w-6 h-6 text-[9px]" />
                                    <span className={`text-sm font-bold truncate text-left ${isSelected ? (isFinal ? (tid === s.winner_team_id ? "text-emerald-400" : "text-red-400") : "text-volt") : "text-white"}`}>{nameFor(tid)}</span>
                                </div>
                                {isSelected && <Check className={`w-4 h-4 shrink-0 ml-2 ${isFinal ? (tid === s.winner_team_id ? "text-emerald-400" : "text-red-400") : "text-volt"}`} />}
                            </button>
                        );
                    })}
                </div>
                <div>
                    <span className="text-xs text-neutral-500 font-mono mb-2 block uppercase tracking-widest">Games</span>
                    <div className="flex justify-between gap-2">
                        {(s._validGames || []).map((g) => {
                            const isPicked = pick.game_count === g;
                            let pillClass = isPicked
                                ? "bg-cyber text-black border-cyber font-bold"
                                : "border-neutral-700 bg-neutral-950 text-zinc-300 hover:border-neutral-500";
                            if (isFinal && isPicked) {
                                pillClass = s.deciding_game === g
                                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500 font-bold"
                                    : "bg-red-500/20 text-red-300 border-red-500 font-bold";
                            }
                            return (
                                <button
                                    key={g}
                                    data-testid={`mlb-series-${s.id}-game-${g}`}
                                    aria-pressed={isPicked}
                                    disabled={locked || !pick.team_id}
                                    onClick={() => selectPick(s.id, pick.team_id, g)}
                                    className={`pill flex-1 py-2 text-center border-2 font-medium transition-colors
                                        ${pillClass}
                                        ${(locked || !pick.team_id) && !isFinal ? "opacity-40 cursor-not-allowed" : (locked || !pick.team_id) ? "cursor-not-allowed" : ""}`}
                                >
                                    in {g}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="mlb-series-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="brutal-card max-w-lg w-full max-h-[90dvh] overflow-y-auto border-volt"
        >
            <div className="sticky top-0 z-10 bg-[#121212] border-b-2 border-neutral-800 px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                <h3 className="font-display font-extrabold uppercase text-lg flex items-center gap-2">
                    <Baseball size={18} weight="duotone" />
                    Series Predictor
                </h3>
                <button
                    onClick={onClose}
                    className="brutal-btn-ghost p-2 shrink-0"
                    data-testid="close-mlb-series-pick-modal-btn"
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="space-y-4 p-4 sm:p-6">
                {!notSetYet && (
                    <div data-testid="mlb-series-how-it-works" className="text-neutral-400 text-[11px] pb-4 border-b-2 border-neutral-800 flex gap-2 items-start text-left">
                        <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                        <p className="leading-relaxed">
                            <span className="text-white font-bold uppercase tracking-tight">How it works:</span> pick a winner + exact game
                            count for <span className="text-white font-semibold">every</span> series below.
                            Right team = <span className="text-cyber font-semibold">+6</span> (+4 more if the game
                            count is exact too). Wrong team = <span className="text-heat font-semibold">−2</span>
                            {" "}(+2 back if you still nailed the game count). Picks lock once that series' Game 1 starts.
                        </p>
                    </div>
                )}

                {anyProjected && (
                    <div
                        data-testid="mlb-series-projected-top-banner"
                        className="bg-amber-500/10 border-2 border-amber-500 text-amber-400 p-3 rounded-lg text-[11px] flex items-start gap-2 text-left"
                    >
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <p className="leading-relaxed">
                            <span className="font-bold uppercase tracking-tight">Provisional:</span> these are today's Wild Card
                            projections based on current standings. You can pick now, but the actual teams/seeds can
                            still shift until the regular season ends. Double-check (and update if needed) your
                            picks once seeding is official.
                        </p>
                    </div>
                )}

                {notSetYet ? (
                    <p data-testid="mlb-series-not-set-notice" className="text-xs text-zinc-500">
                        Playoff teams have not been determined yet — check back once {rounds[0]?.label || "Wild Card"} matchups are set.
                    </p>
                ) : (
                    <div className="space-y-4">
                        {openSeries.length > 0 ? (
                            <div className="space-y-3">
                                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold" data-testid="mlb-series-open-heading">
                                    Open — Make Your Pick
                                </p>
                                {openSeries.map(renderSeriesCard)}
                            </div>
                        ) : (
                            <p data-testid="mlb-series-no-open-notice" className="text-xs text-zinc-500">
                                No open series right now — check back once new matchups are set.
                            </p>
                        )}

                        {/* iter87.299/87.315 (user-requested) — locked-but-
                            not-decided series show here, collapsible,
                            starting OPEN — grouped by round since more than
                            one round can be live at once (e.g. a lagging
                            Wild Card series still open while an early
                            Division Series is already underway).
                            iter87.317 (user-reported) — always render this
                            section (with an empty-state message), same as
                            the Open section above, instead of hiding the
                            whole header when there's nothing live/locked
                            yet — a fully-hidden section reads like a bug,
                            not "nothing to show here". */}
                        <Collapsible open={showLiveLocked} onOpenChange={setShowLiveLocked}>
                            <CollapsibleTrigger
                                data-testid="mlb-series-live-toggle"
                                className="flex items-center justify-between w-full py-2 border-t-2 border-neutral-800 text-left"
                            >
                                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
                                    <Lock className="w-3 h-3" /> Locked & Live ({liveSeries.length})
                                </span>
                                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showLiveLocked ? "rotate-180" : ""}`} />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-4 pt-3" data-testid="mlb-series-live-content">
                                {liveSeries.length > 0 ? (
                                    Object.entries(liveByRound).map(([roundKey, group]) => (
                                        <div key={roundKey} className="space-y-3">
                                            <p className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{group.label}</p>
                                            {group.series.map(renderSeriesCard)}
                                        </div>
                                    ))
                                ) : (
                                    <p data-testid="mlb-series-live-empty-notice" className="text-xs text-zinc-500">
                                        Nothing locked or in progress right now — series move here once their Game 1 starts.
                                    </p>
                                )}
                            </CollapsibleContent>
                        </Collapsible>

                        {/* iter87.315 (user-requested) — a series that has
                            actually concluded moves here instead of staying
                            in Locked & Live — read-only recap, collapsed by
                            default (checked less often than in-progress
                            series). iter87.317 — same always-render fix as
                            Locked & Live above. */}
                        <Collapsible open={showCompleted} onOpenChange={setShowCompleted}>
                            <CollapsibleTrigger
                                data-testid="mlb-series-completed-toggle"
                                className="flex items-center justify-between w-full py-2 border-t-2 border-neutral-800 text-left"
                            >
                                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
                                    <Trophy className="w-3 h-3" /> Completed ({completedSeries.length})
                                </span>
                                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showCompleted ? "rotate-180" : ""}`} />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-4 pt-3" data-testid="mlb-series-completed-content">
                                {completedSeries.length > 0 ? (
                                    Object.entries(completedByRound).map(([roundKey, group]) => (
                                        <div key={roundKey} className="space-y-3">
                                            <p className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{group.label}</p>
                                            {group.series.map(renderSeriesCard)}
                                        </div>
                                    ))
                                ) : (
                                    <p data-testid="mlb-series-completed-empty-notice" className="text-xs text-zinc-500">
                                        No series have finished yet — check back once one wraps up.
                                    </p>
                                )}
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
                )}
            </div>
            {/* iter87.283 (user-reported) — Save/Clear float as a
                sticky footer docked to the modal's bottom, matching
                "Your Season Picks" `PickSlip`. No negative-margin
                edge-bleed here (that caused a horizontal scrollbar —
                the modal panel has no `overflow-x-hidden` to contain
                it) — the footer just fills its own parent's width. */}
            {!notSetYet && (
                <div className="sticky bottom-0 z-10 mt-4 flex items-center gap-2 bg-[#121212] border-t-2 border-neutral-800 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <button
                        type="button"
                        onClick={onClearAll}
                        disabled={clearing || savingAll}
                        data-testid="mlb-series-clear-all-btn"
                        className="brutal-btn-ghost flex-1 py-2.5 text-xs font-bold uppercase tracking-widest disabled:opacity-40"
                    >
                        {clearing ? "Clearing…" : "Clear All Picks"}
                    </button>
                    <button
                        type="button"
                        onClick={onSavePicks}
                        disabled={savingAll || clearing || incompleteCount > 0}
                        data-testid="mlb-series-save-picks-btn"
                        title={incompleteCount > 0 ? `Pick a team and a game count for every series first — ${incompleteCount} incomplete` : undefined}
                        className="brutal-btn flex-1 py-2.5 text-xs font-bold uppercase tracking-widest bg-volt text-black border-volt disabled:opacity-40"
                    >
                        {savingAll ? "Saving…" : "Save Picks"}
                    </button>
                </div>
            )}
        </BentoModal>
    );
}
